<script>
  import { _ } from "svelte-i18n";
  import { createEventDispatcher } from "svelte";
  import { api } from "../../utilities/Utils.svelte";
  import { assessmentId, userId, userName, sharedTimer, sharedTimerMode } from "../../utilities/DataStore";
  import { AzureBlobStorageService } from "../../utilities/azureBlobStorage.js";
  import html2canvas from "html2canvas";

  export let instanceId = "";
  export let exerciseId = "";

  // Exercise progress details
  export let exerciseType = "";
  export let exerciseName = "";
  export let currentStep = null;
  export let totalSteps = null;
  export let buttonState = "";
  export let invitationNumber = "";

  const dispatch = createEventDispatcher();

  let isOpen = false;
  let message = "";
  let errorMessage = "";
  let isSubmitting = false;
  let successMessage = "";
  let isCapturingScreenshot = false;

  // { blob: Blob, objectUrl: string, name: string } | null
  let autoScreenshot = null;
  // Array of { file: File, objectUrl: string }
  let manualAttachments = [];

  // ── Screenshot capture ──────────────────────────────────────────────────────

  async function captureScreenshot() {
    isCapturingScreenshot = true;
    try {
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        // Exclude the modal overlay itself if it somehow rendered first
        ignoreElements: (el) => el.classList.contains("report-overlay"),
      });
      const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/png"),
      );
      const objectUrl = URL.createObjectURL(blob);
      autoScreenshot = { blob, objectUrl, name: `screenshot-${Date.now()}.png` };
    } catch (e) {
      console.warn("Screenshot capture failed:", e);
    } finally {
      isCapturingScreenshot = false;
    }
  }

  // ── Modal open / close ──────────────────────────────────────────────────────

  async function openModal() {
    // Capture the page BEFORE the overlay is rendered
    await captureScreenshot();
    isOpen = true;
    errorMessage = "";
    successMessage = "";
  }

  function closeModal() {
    isOpen = false;
    errorMessage = "";
    successMessage = "";
    if (autoScreenshot?.objectUrl) URL.revokeObjectURL(autoScreenshot.objectUrl);
    autoScreenshot = null;
    for (const a of manualAttachments) URL.revokeObjectURL(a.objectUrl);
    manualAttachments = [];
    message = "";
  }

  function handleOverlayClick(e) {
    if (e.target === e.currentTarget) closeModal();
  }

  function handleKeydown(e) {
    if (e.key === "Escape" && isOpen) closeModal();
  }

  // ── Manual file attachments ─────────────────────────────────────────────────

  function handleFileChange(e) {
    const files = Array.from(e.target.files || []);
    const added = files.map((file) => ({
      file,
      objectUrl: URL.createObjectURL(file),
    }));
    manualAttachments = [...manualAttachments, ...added];
    e.target.value = "";
  }

  function removeManualAttachment(index) {
    URL.revokeObjectURL(manualAttachments[index].objectUrl);
    manualAttachments = manualAttachments.filter((_, i) => i !== index);
  }

  function removeScreenshot() {
    if (autoScreenshot?.objectUrl) URL.revokeObjectURL(autoScreenshot.objectUrl);
    autoScreenshot = null;
  }

  // ── Azure upload ────────────────────────────────────────────────────────────

  async function uploadToBugReports(blob, fileName, contentType) {
    const azureService = new AzureBlobStorageService();
    const uploadUrl = `${azureService.containerUrl}/bug-reports/${fileName}?${azureService.sasToken}`;
    const response = await fetch(uploadUrl, {
      method: "PUT",
      headers: {
        "x-ms-blob-type": "BlockBlob",
        "Content-Type": contentType,
      },
      body: blob,
    });
    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }
    return uploadUrl.split("?")[0]; // public URL without SAS token
  }

  // ── Submit ──────────────────────────────────────────────────────────────────

  async function handleSubmit(e) {
    e.preventDefault();
    errorMessage = "";

    if (!message?.trim()) {
      errorMessage = "Please describe the issue.";
      return;
    }

    isSubmitting = true;

    try {
      const ts = Date.now();
      const attachmentUrls = [];

      if (autoScreenshot) {
        const url = await uploadToBugReports(
          autoScreenshot.blob,
          `${ts}-auto-screenshot.png`,
          "image/png",
        );
        attachmentUrls.push(url);
      }

      for (const { file } of manualAttachments) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const url = await uploadToBugReports(
          file,
          `${ts}-${safeName}`,
          file.type || "application/octet-stream",
        );
        attachmentUrls.push(url);
      }

      const payload = {
        name: $userName || "",
        email: $userId || "",
        assessmentId: $assessmentId || invitationNumber || "",
        commentText: message,
        data: JSON.stringify({
          instanceId,
          exerciseId,
          exerciseType,
          exerciseName,
          currentStep,
          totalSteps,
          timerMode: $sharedTimerMode,
          timerRemaining: $sharedTimer,
          buttonState,
          pageUrl: location.href,
          userAgent: navigator.userAgent,
          reportedAt: new Date().toISOString(),
          invitationNumber,
          assessmentId: $assessmentId,
          attachments: attachmentUrls,
        }),
      };

      await api
        .post("public/assessment/bug-report", {
          headers: { "Content-Type": "application/json" },
          json: payload,
        })
        .json();

      console.log("Bug report submitted:", payload);

      successMessage = "Thank you! Your report has been submitted.";
      dispatch("reported", payload);

      setTimeout(() => closeModal(), 1500);
    } catch (err) {
      console.error("Error submitting report:", err);
      errorMessage = "Failed to submit report. Please try again.";
    } finally {
      isSubmitting = false;
    }
  }
</script>

<svelte:window on:keydown={handleKeydown} />

<!-- Floating Action Button -->
<button
  class="report-fab"
  on:click={openModal}
  disabled={isCapturingScreenshot}
  aria-label="Report an issue"
  title="Report an issue"
>
  {#if isCapturingScreenshot}
    <span class="report-fab-spinner" aria-hidden="true"></span>
  {:else}
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 3l9 16H3L12 3z" stroke="currentColor" stroke-width="2" />
      <path
        d="M12 9v4"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
      <path
        d="M12 17h.01"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
      />
    </svg>
  {/if}
  <span class="report-fab-text">
    {isCapturingScreenshot ? $_("CAPTURING_SCREENSHOT") : $_("REPORT_ISSUE")}
  </span>
</button>

<!-- Modal Overlay -->
{#if isOpen}
  <div
    class="report-overlay"
    on:click={handleOverlayClick}
    role="presentation"
  >
    <section
      class="report-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="reportTitle"
    >
      <header>
        <h2 id="reportTitle">{$_("REPORT_ISSUE")}</h2>
        <button
          class="report-close"
          on:click={closeModal}
          aria-label="Close dialog"
        >
          ✕
        </button>
      </header>

      <form on:submit={handleSubmit}>
        <div class="report-content">
          {#if errorMessage}
            <div class="report-error">
              {errorMessage}
            </div>
          {/if}

          {#if successMessage}
            <div class="report-success">
              {successMessage}
            </div>
          {/if}

          <label>
            {$_("WHAT_HAPPENED")}
            <textarea
              bind:value={message}
              required
              placeholder=""
            ></textarea>
            <span class="helper-text">{$_("DESCRIBE_ISSUE")}</span>
          </label>

          <!-- Attachments -->
          <div class="attachments-section">
            <div class="attachments-header">
              <span class="attachments-label">{$_("ATTACHMENTS")}</span>
              <label class="attach-add-btn" title={$_("ADD_IMAGE")}>
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  on:change={handleFileChange}
                  class="visually-hidden"
                />
                {$_("ADD_IMAGE")}
              </label>
            </div>

            <p class="attach-hint">{$_("ATTACH_FILES_HINT")}</p>

            {#if manualAttachments.length > 0}
              <ul class="attachments-list">
                {#each manualAttachments as att, i}
                  <li class="attachment-item">
                    {#if att.file.type.startsWith("video/")}
                      <video
                        src={att.objectUrl}
                        class="attachment-thumb"
                        muted
                        preload="metadata"
                      ></video>
                    {:else}
                      <img
                        src={att.objectUrl}
                        alt={att.file.name}
                        class="attachment-thumb"
                      />
                    {/if}
                    <span class="attachment-name">{att.file.name}</span>
                    <button
                      type="button"
                      class="attachment-remove"
                      on:click={() => removeManualAttachment(i)}
                      aria-label="Remove {att.file.name}"
                    >✕</button>
                  </li>
                {/each}
              </ul>
            {/if}
          </div>

          <div class="report-hint">
            {@html $_("REPORT_ISSUE_HINT")}
          </div>
        </div>

        <div class="report-actions">
          <button type="button" class="report-btn" on:click={closeModal}>
            {$_("CANCEL")}
          </button>
          <button
            type="submit"
            class="report-btn primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? $_("SENDING") : $_("SUBMIT_REPORT")}
          </button>
        </div>
      </form>
    </section>
  </div>
{/if}

<style>
  .report-fab {
    position: fixed;
    right: 20px;
    bottom: 20px;
    height: 56px;
    min-width: 180px;
    padding: 0 20px;
    border: 0;
    border-radius: 16px;
    background: #fbbf24;
    color: #111827;
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.18);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    z-index: 9999;
    transition: filter 0.15s, transform 0.1s, box-shadow 0.15s;
    font-weight: 600;
    font-size: 14px;
    text-transform: none;
    letter-spacing: 0;
  }
  .report-fab:hover:not(:disabled) {
    filter: brightness(1.05);
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.2);
  }
  .report-fab:active:not(:disabled) {
    transform: translateY(1px);
  }
  .report-fab:focus-visible {
    outline: 3px solid rgba(255, 255, 255, 0.55);
    outline-offset: 3px;
  }
  .report-fab:disabled {
    opacity: 0.75;
    cursor: wait;
  }
  .report-fab svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }
  .report-fab-text {
    white-space: nowrap;
  }
  .report-fab-spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(0, 0, 0, 0.25);
    border-top-color: #000;
    border-radius: 50%;
    animation: fab-spin 0.7s linear infinite;
    flex-shrink: 0;
  }
  @keyframes fab-spin {
    to { transform: rotate(360deg); }
  }

  /* Modal overlay */
  .report-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px;
    z-index: 10000;
  }

  /* Modal card */
  .report-modal {
    width: min(560px, 100%);
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 14px;
    box-shadow: 0 18px 60px rgba(0, 0, 0, 0.3);
    overflow: hidden;
  }

  .report-modal header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 16px 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    background: #f8fafc;
  }

  .report-modal header h2 {
    font-size: 16px;
    margin: 0;
    font-weight: 650;
    letter-spacing: 0.2px;
    color: #1e293b;
  }

  .report-close {
    background: transparent;
    border: 0;
    color: #64748b;
    cursor: pointer;
    border-radius: 10px;
    padding: 8px;
    font-size: 16px;
    line-height: 1;
  }
  .report-close:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #1e293b;
  }
  .report-close:focus-visible {
    outline: 2px solid rgba(37, 99, 235, 0.55);
    outline-offset: 2px;
  }

  .report-content {
    padding: 16px;
    display: grid;
    gap: 12px;
  }

  .report-modal label {
    display: grid;
    gap: 6px;
    font-size: 13px;
    color: #374151;
    font-weight: 500;
  }

  .report-modal input,
  .report-modal textarea,
  .report-modal select {
    width: 100%;
    box-sizing: border-box;
    padding: 10px 12px;
    border-radius: 10px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #1e293b;
    outline: none;
    font-size: 14px;
  }

  .report-modal textarea {
    min-height: 120px;
    resize: vertical;
    font-family: inherit;
  }

  .report-modal input:focus,
  .report-modal textarea:focus,
  .report-modal select:focus {
    border-color: rgba(255, 204, 7, 0.75);
    box-shadow: 0 0 0 4px rgba(255, 204, 7, 0.18);
  }

  .report-hint {
    color: #6b7280;
    font-size: 12px;
  }

  .report-hint kbd {
    background: #e5e7eb;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
  }

  .helper-text {
    color: #6b7280;
    font-size: 12px;
    font-weight: 400;
    margin-top: 4px;
  }

  .report-actions {
    display: flex;
    gap: 10px;
    justify-content: flex-end;
    padding: 14px 16px 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.12);
    background: #f8fafc;
  }

  .report-btn {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    border-radius: 10px;
    padding: 10px 16px;
    cursor: pointer;
    font-weight: 600;
    font-size: 14px;
    transition: background 0.15s;
  }
  .report-btn:hover {
    background: #f3f4f6;
  }
  .report-btn.primary {
    border-color: #00a094;
    background: #00a094ff;
    color: #fff;
  }
  .report-btn.primary:hover {
    filter: brightness(1.05);
  }
  .report-btn.primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .report-error {
    color: #dc2626;
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.28);
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
  }

  .report-success {
    color: #16a34a;
    background: rgba(22, 163, 74, 0.1);
    border: 1px solid rgba(22, 163, 74, 0.28);
    padding: 10px 12px;
    border-radius: 10px;
    font-size: 13px;
  }

  .readonly-input {
    background-color: #f3f4f6 !important;
    color: #6b7280 !important;
    cursor: not-allowed;
  }

  /* Attachments */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .attachments-section {
    display: grid;
    gap: 8px;
  }

  .attachments-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .attachments-label {
    font-size: 13px;
    font-weight: 500;
    color: #374151;
  }

  .attach-add-btn {
    font-size: 12px;
    font-weight: 600;
    color: #00a094;
    cursor: pointer;
    padding: 4px 10px;
    border: 1px solid #00a094;
    border-radius: 8px;
    transition: background 0.15s;
    user-select: none;
  }
  .attach-add-btn:hover {
    background: rgba(0, 160, 148, 0.08);
  }

  .attach-hint {
    margin: 0;
    font-size: 12px;
    color: #6b7280;
  }

  .attachments-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 6px;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 6px 8px;
  }

  .attachment-thumb {
    width: 48px;
    height: 36px;
    object-fit: cover;
    border-radius: 4px;
    flex-shrink: 0;
    border: 1px solid #e5e7eb;
  }

  .attachment-name {
    flex: 1;
    font-size: 12px;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .attachment-remove {
    background: transparent;
    border: 0;
    color: #9ca3af;
    cursor: pointer;
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 13px;
    line-height: 1;
    flex-shrink: 0;
  }
  .attachment-remove:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #dc2626;
  }
</style>
